import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Line, Group, Circle, Shape } from 'react-konva';
import { useTranslation } from 'react-i18next';
import containerImageSrc from '../../../images/container.png'; // Assuming image is here
import tireImageSrc from '../../../images/pneumatico.png';
import railwayImageSrc from '../../../images/train-rail.png';
// Importa le texture se vuoi usarle, altrimenti rimuovi
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/basecourse.png';
import texture2RailwayImage from '../../../images/stone-ballast-rail.png'; // Railway ballast texture
import texture3Image from '../../../images/subbase.png';
import texture4Image from '../../../images/subgrade.png';
import { IGeogridItem } from '../../../api/IApiResponse';
import { unit as unitEnum } from '../../../enum/project'; // Import unit enum

interface Props {
    surfaceLayerThickness: number;
    baseCourseThickness: number;
    subbaseCourseThickness: number;
    baseCourseGeogrids?: IGeogridItem[];
    subbaseCourseGeogrids?: IGeogridItem[];
    subgradeVisibleHeight?: number;
    geogridType?: 'Rectangular' | 'Circular' | 'Railway';
    unit?: unitEnum; // Add unit prop
}

const defaultSurfaceLayerThickness = 0.15;
const defaultBaseCourseThickness = 0.15;
const defaultSubbaseCourseThickness = 0.25;
const defaultSubgradeHeight = 0.5;

// Layout constants
const IMAGE_TOP_PADDING = 10;
const CONTAINER_IMAGE_HEIGHT = 120;
const CONTAINER_IMAGE_WIDTH_FACTOR = 0.8; // As a factor of squareWidth
const SPACING_AFTER_IMAGE = 0; // Space between image and first layer
const STAGE_BOTTOM_PADDING = 10; // Optional padding at the bottom of the stage
const FIXED_TOTAL_RECTANGLE_HEIGHT = 400; // Fixed total height for all rectangles combined

const GeogridLayers: React.FC<Props> = ({
    surfaceLayerThickness = defaultSurfaceLayerThickness,
    baseCourseThickness = defaultBaseCourseThickness,
    subbaseCourseThickness = defaultSubbaseCourseThickness,
    baseCourseGeogrids = [],
    subbaseCourseGeogrids = [],
    subgradeVisibleHeight = defaultSubgradeHeight,
    geogridType = 'Rectangular',
    unit = unitEnum.SI, // Default to Metric (SI)
}) => {
    const { t } = useTranslation();
    // Ensure minimum values to prevent NaN calculations
    const safeSurfaceLayerThickness = Math.max(0, surfaceLayerThickness || 0);
    const safeBaseCourseThickness = Math.max(0.01, baseCourseThickness || 0.01); // Minimum 0.01 to prevent division by zero
    const safeSubbaseCourseThickness = Math.max(0.01, subbaseCourseThickness || 0.01);
    const safeSubgradeVisibleHeight = Math.max(0.01, subgradeVisibleHeight || 0.01);

    // Calcola il totale degli spessori per la proporzionalità
    // Escludiamo il surface layer se il suo spessore è 0
    const effectiveSurfaceThickness = safeSurfaceLayerThickness > 0 ? safeSurfaceLayerThickness : 0;
    const totalThickness = effectiveSurfaceThickness + safeBaseCourseThickness + safeSubbaseCourseThickness + safeSubgradeVisibleHeight;

    // Ensure totalThickness is never zero
    const safeTotalThickness = Math.max(0.01, totalThickness);

    // Calcola le altezze proporzionali in pixel per ogni layer
    const surfaceLayerHeightPx = effectiveSurfaceThickness > 0 ? (effectiveSurfaceThickness / safeTotalThickness) * FIXED_TOTAL_RECTANGLE_HEIGHT : 0;
    const baseCourseHeightPx = (safeBaseCourseThickness / safeTotalThickness) * FIXED_TOTAL_RECTANGLE_HEIGHT;
    const subbaseCourseHeightPx = (safeSubbaseCourseThickness / safeTotalThickness) * FIXED_TOTAL_RECTANGLE_HEIGHT;
    const subgradeHeightPx = (safeSubgradeVisibleHeight / safeTotalThickness) * FIXED_TOTAL_RECTANGLE_HEIGHT;

    // Scale for geogrid positioning within each layer (maintains relative positioning)
    const baseCourseScale = baseCourseHeightPx / safeBaseCourseThickness;
    const subbaseCourseScale = subbaseCourseHeightPx / safeSubbaseCourseThickness;

    const unitLabel = unit === unitEnum.Imperial ? 'ft' : 'm';

    const squareWidth = 300;
    const titleAreaWidth = 150;
    const rectStartX = titleAreaWidth + 10;
    const rectEndX = rectStartX + squareWidth;
    const arrowLength = 30;
    const arrowGap = 5;
    const labelGap = -20;
    const labelX = rectStartX + squareWidth + arrowGap + arrowLength + labelGap;
    const stageWidth = labelX + 50;

    // Larghezza standard per l'immagine del container
    const containerImageWidth = squareWidth * CONTAINER_IMAGE_WIDTH_FACTOR;
    // Larghezza specifica per l'immagine del pneumatico (un terzo di quella del container, come da codice esistente)
    const tireImageWidth = containerImageWidth / 3;
    // Larghezza per l'immagine del treno (simile al pneumatico - stretta)
    const railwayImageWidth = tireImageWidth;

    // Altezza base per l'immagine del container
    const containerActualHeight = CONTAINER_IMAGE_HEIGHT;
    // Altezza specifica per l'immagine del pneumatico (doppia di quella del container)
    const tireActualHeight = CONTAINER_IMAGE_HEIGHT * 1.5;
    // Altezza per l'immagine del treno (simile al pneumatico - alta)
    const railwayActualHeight = tireActualHeight;

    // Determina la larghezza e l'altezza dell'immagine da usare in base al geogridType
    const imageToRenderWidth = geogridType === 'Circular' ? tireImageWidth : geogridType === 'Railway' ? railwayImageWidth : containerImageWidth;

    // Determina l'altezza dell'immagine da usare:
    // Se 'Circular' (pneumatico), usa tireActualHeight.
    // Se 'Railway' (treno), usa railwayActualHeight.
    // Altrimenti (container), usa containerActualHeight.
    const imageToRenderHeight = geogridType === 'Circular' ? tireActualHeight : geogridType === 'Railway' ? railwayActualHeight : containerActualHeight;

    // Stato per immagini e texture
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [texture1Img, setTexture1Img] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture2RailwayImg, setTexture2RailwayImg] = useState<HTMLImageElement | null>(null);
    const [texture3Img, setTexture3Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    // Texture loading state tracking
    const [texturesLoaded, setTexturesLoaded] = useState({
        texture1: false,
        texture2: false,
        texture2Railway: false,
        texture3: false,
        texture4: false,
    });

    useEffect(() => {
        const img = new window.Image();
        img.src = geogridType === 'Circular' ? tireImageSrc : geogridType === 'Railway' ? railwayImageSrc : containerImageSrc;
        img.onload = () => setImage(img);
        img.onerror = () => {};

        // Reset texture loading state
        setTexturesLoaded({ texture1: false, texture2: false, texture2Railway: false, texture3: false, texture4: false });

        const tex1 = new window.Image();
        tex1.crossOrigin = 'anonymous'; // Add CORS support for better texture loading
        tex1.src = texture1Image;
        tex1.onload = () => {
            setTexture1Img(tex1);
            setTexturesLoaded(prev => ({ ...prev, texture1: true }));
        };
        tex1.onerror = e => {
            setTexturesLoaded(prev => ({ ...prev, texture1: false }));
        };

        const tex2 = new window.Image();
        tex2.crossOrigin = 'anonymous';
        tex2.src = texture2Image;
        tex2.onload = () => {
            setTexture2Img(tex2);
            setTexturesLoaded(prev => ({ ...prev, texture2: true }));
        };
        tex2.onerror = e => {
            setTexturesLoaded(prev => ({ ...prev, texture2: false }));
        };

        // Load Railway ballast texture
        const tex2Railway = new window.Image();
        tex2Railway.crossOrigin = 'anonymous';
        tex2Railway.src = texture2RailwayImage;
        tex2Railway.onload = () => {
            setTexture2RailwayImg(tex2Railway);
            setTexturesLoaded(prev => ({ ...prev, texture2Railway: true }));
        };
        tex2Railway.onerror = e => {
            setTexturesLoaded(prev => ({ ...prev, texture2Railway: false }));
        };

        const tex3 = new window.Image();
        tex3.crossOrigin = 'anonymous';
        tex3.src = texture3Image;
        tex3.onload = () => {
            setTexture3Img(tex3);
            setTexturesLoaded(prev => ({ ...prev, texture3: true }));
        };
        tex3.onerror = e => {
            setTexturesLoaded(prev => ({ ...prev, texture3: false }));
        };

        const tex4 = new window.Image();
        tex4.crossOrigin = 'anonymous';
        tex4.src = texture4Image;
        tex4.onload = () => {
            setTexture4Img(tex4);
            setTexturesLoaded(prev => ({ ...prev, texture4: true }));
        };
        tex4.onerror = e => {
            setTexturesLoaded(prev => ({ ...prev, texture4: false }));
        };
    }, [geogridType]);

    // Ref e stato per larghezza/altezza testo geogrid
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const geogridTextRef = React.useRef<any>(null);
    const [geogridTextWidth, setGeogridTextWidth] = useState(0);
    const [geogridTextHeight, setGeogridTextHeight] = useState(0);

    // Array di ref e stato per larghezza/altezza testo geogrid base course
    const geogridBaseTextRefs = useRef([] as any[]);
    const [geogridBaseTextWidths, setGeogridBaseTextWidths] = useState<number[]>([]);
    const [geogridBaseTextHeights, setGeogridBaseTextHeights] = useState<number[]>([]);

    useEffect(() => {
        if (baseCourseGeogrids) {
            const widths = baseCourseGeogrids.map((_, i) => geogridBaseTextRefs.current[i]?.getTextWidth?.() || 0);
            const heights = baseCourseGeogrids.map((_, i) => geogridBaseTextRefs.current[i]?.height?.() || 0);
            setGeogridBaseTextWidths(widths);
            setGeogridBaseTextHeights(heights);
        }
    }, [baseCourseGeogrids]);

    // Array di ref e stato per larghezza/altezza testo geogrid subbase
    const geogridSubbaseTextRefs = useRef([] as any[]);
    const [geogridSubbaseTextWidths, setGeogridSubbaseTextWidths] = useState<number[]>([]);
    const [geogridSubbaseTextHeights, setGeogridSubbaseTextHeights] = useState<number[]>([]);

    useEffect(() => {
        if (subbaseCourseGeogrids) {
            const widths = subbaseCourseGeogrids.map((_, i) => geogridSubbaseTextRefs.current[i]?.getTextWidth?.() || 0);
            const heights = subbaseCourseGeogrids.map((_, i) => geogridSubbaseTextRefs.current[i]?.height?.() || 0);
            setGeogridSubbaseTextWidths(widths);
            setGeogridSubbaseTextHeights(heights);
        }
    }, [subbaseCourseGeogrids]);

    // Y coordinates for layout
    const imageYPos = IMAGE_TOP_PADDING;
    // Surface Layer parte subito dopo l'immagine (solo se visibile)
    const surfaceY = imageYPos + imageToRenderHeight + SPACING_AFTER_IMAGE;
    // Base Course inizia dopo Surface Layer se presente, altrimenti subito dopo l'immagine
    const baseY = surfaceLayerThickness > 0 ? surfaceY + surfaceLayerHeightPx : surfaceY;
    const subbaseY = baseY + baseCourseHeightPx;
    const subgradeY = subbaseY + subbaseCourseHeightPx;

    // Calcola l'altezza totale richiesta per lo stage
    const totalHeight = Math.max(100, surfaceY + FIXED_TOTAL_RECTANGLE_HEIGHT + STAGE_BOTTOM_PADDING);

    // Get translated titles with Railway-specific labels
    const baseCourseTitle = geogridType === 'Railway' ? t('geogrid_base_course_railway') : t('geogrid_base_course');
    const subbaseCourseTitle = geogridType === 'Railway' ? t('geogrid_subbase_course_railway') : t('geogrid_subbase_course');
    const subgradeTitle = t('geogrid_subgrade_course');
    const surfaceLayerTitle = t('geogrid_surface_layer');
    
    const titles = [baseCourseTitle, subbaseCourseTitle, subgradeTitle, surfaceLayerTitle];
    const layerColors = ['#444444', '#90EE90', '#DEB887'];

    // Fallback colors for each layer when textures fail to load
    const fallbackColors = {
        surface: '#2C2C2C', // Dark gray for asphalt (surface layer)
        base: '#8B4513', // Saddle brown for base course
        subbase: '#DAA520', // Goldenrod for subbase course
        subgrade: '#CD853F', // Peru for subgrade
    };

    const geogridColor = '#24244f';
    const geogridColor2 = 'red';
    const colorTips = 'red';
    const geogridStrokeWidth = 2;

    // custom riga GEOGRID
    const nPoints = 6;
    const cerchioRaggio = 6;
    const circleSpacing = Math.max(10, (rectEndX - (rectStartX + 0)) / (nPoints - 1)); // Ensure minimum spacing
    const circleXs = Array.from({ length: nPoints }, (_, i) => rectStartX - 6 + i * circleSpacing);
    const geogridLabelPadding = 20;

    // posizine del label geogrid:

    const X_geogridLabelGroup = rectStartX + squareWidth / 2 - (Math.max(0, geogridTextWidth) + geogridLabelPadding) / 2;
    const Y_geogridLabelGroup = Math.max(0, geogridTextHeight) + geogridLabelPadding / 2;

    const font_right_dimension = 12;

    // Check if all required images are loaded
    const allImagesLoaded = React.useMemo(() => {
        // Main image must be loaded (tire, container, or railway)
        if (!image) return false;

        // Check texture images based on which layers are visible
        const requiredTextures = [];
        if (surfaceLayerThickness > 0) requiredTextures.push(texturesLoaded.texture1);
        if (baseCourseThickness > 0) requiredTextures.push(texturesLoaded.texture2);
        if (subbaseCourseThickness > 0) requiredTextures.push(texturesLoaded.texture3);
        if (subgradeVisibleHeight > 0) requiredTextures.push(texturesLoaded.texture4);

        // All required textures must be loaded
        return requiredTextures.every(loaded => loaded === true);
    }, [image, texturesLoaded, surfaceLayerThickness, baseCourseThickness, subbaseCourseThickness, subgradeVisibleHeight]);

    // Set data attribute on parent to indicate loading state
    React.useEffect(() => {
        const canvasContainer = document.getElementById('canvas-container');
        if (canvasContainer) {
            canvasContainer.setAttribute('data-images-loaded', allImagesLoaded.toString());
            if (allImagesLoaded) {
            }
        }
    }, [allImagesLoaded]);

    return (
        <Stage width={Math.max(100, stageWidth)} height={Math.max(100, totalHeight)}>
            <Layer>
                {/* Container Image */}
                {image && (
                    <KonvaImage
                        image={image}
                        x={Math.max(0, geogridType === 'Railway' ? rectStartX + squareWidth - imageToRenderWidth - 30 : rectStartX + squareWidth / 2 - imageToRenderWidth / 2)}
                        y={Math.max(0, imageYPos)}
                        width={Math.max(10, imageToRenderWidth)}
                        height={Math.max(10, imageToRenderHeight)}
                    />
                )}

                {/* === Surface Layer === */}
                {surfaceLayerThickness > 0 && (
                    <>
                        <Text
                            text={`${titles[3]}: ${surfaceLayerThickness.toFixed(2)} ${unitLabel}`}
                            x={5}
                            y={surfaceY + surfaceLayerHeightPx / 2 - 6}
                            width={titleAreaWidth - 10}
                            align="right"
                            fontSize={12}
                            fill="black"
                        />
                        <Rect
                            x={Math.max(0, rectStartX)}
                            y={Math.max(0, surfaceY)}
                            width={Math.max(1, squareWidth)}
                            height={Math.max(1, surfaceLayerHeightPx)}
                            fillPatternImage={texturesLoaded.texture1 && texture1Img ? texture1Img : undefined}
                            fillPatternRepeat="repeat"
                            fillPatternScale={{ x: 0.2, y: 0.2 }}
                            fill={!texturesLoaded.texture1 || !texture1Img ? fallbackColors.surface : undefined}
                            stroke="grey"
                            strokeWidth={0.5}
                        />
                    </>
                )}

                {/* === Base Course === */}
                {baseCourseThickness > 0 && (
                    <Text
                        text={`${titles[0]}: ${baseCourseThickness.toFixed(2)} ${unitLabel}`}
                        x={5}
                        y={baseY + baseCourseHeightPx / 2 - 6}
                        width={titleAreaWidth - 10}
                        align="right"
                        fontSize={12}
                        fill="black"
                    />
                )}
                {/* Render Base Course as trapezoid for Railway, rectangular for others */}
                {geogridType === 'Railway' ? (
                    <Shape
                        sceneFunc={(context, shape) => {
                            // Reason: Railway uses trapezoid to show load spreading (40° angle)
                            const ballastHeight = baseCourseHeightPx;
                            const subballastHeight = subbaseCourseHeightPx;
                            const totalTrapezoidHeight = ballastHeight + subballastHeight;
                            const slopeRatio = 0.84; // 40 degrees angle (tan(40°) ≈ 0.84)

                            // Ballast trapezoid: narrow top (under train wheel) → wider bottom
                            // Top width approximately matches train wheel width
                            const trainWheelWidth = imageToRenderWidth + 60; // Train wheel + margin
                            const topStartX = rectStartX + squareWidth - trainWheelWidth; // Start under train
                            const topEndX = rectStartX + squareWidth; // Right edge

                            // Bottom edge spreads outward based on ballast height and slope angle
                            const ballastSlopeOffset = ballastHeight * slopeRatio;
                            const bottomStartX = topStartX - ballastSlopeOffset;
                            const bottomEndX = topEndX; // Right edge stays aligned

                            context.beginPath();
                            context.moveTo(topStartX, baseY); // Top left
                            context.lineTo(topEndX, baseY); // Top right
                            context.lineTo(bottomEndX, baseY + ballastHeight); // Bottom right
                            context.lineTo(bottomStartX, baseY + ballastHeight); // Bottom left
                            context.closePath();

                            // Apply fill color and texture (Railway uses stone-ballast-rail texture)
                            if (texturesLoaded.texture2Railway && texture2RailwayImg) {
                                const pattern = context.createPattern(texture2RailwayImg, 'repeat');
                                if (pattern) {
                                    context.fillStyle = pattern;
                                }
                            } else {
                                context.fillStyle = fallbackColors.base;
                            }
                            context.fill();

                            // Apply stroke
                            context.strokeStyle = 'grey';
                            context.lineWidth = 0.5;
                            context.stroke();

                            context.fillStrokeShape(shape);
                        }}
                    />
                ) : (
                    <Rect
                        x={Math.max(0, rectStartX)}
                        y={Math.max(0, baseY)}
                        width={Math.max(1, squareWidth)}
                        height={Math.max(1, baseCourseHeightPx)}
                        fillPatternImage={texturesLoaded.texture2 && texture2Img ? texture2Img : undefined}
                        fillPatternRepeat="repeat"
                        fillPatternScale={{ x: 0.8, y: 0.8 }}
                        fill={!texturesLoaded.texture2 || !texture2Img ? fallbackColors.base : undefined}
                        stroke="grey"
                        strokeWidth={0.5}
                    />
                )}

                {/* === Subbase Course === */}
                {subbaseCourseThickness > 0 && (
                    <Text
                        text={`${titles[1]}: ${subbaseCourseThickness.toFixed(2)} ${unitLabel}`}
                        x={5}
                        y={subbaseY + subbaseCourseHeightPx / 2 - 6}
                        width={titleAreaWidth - 10}
                        align="right"
                        fontSize={12}
                        fill="black"
                    />
                )}
                {/* Render Subbase Course as trapezoid for Railway, rectangular for others */}
                {geogridType === 'Railway' ? (
                    <Shape
                        sceneFunc={(context, shape) => {
                            // Reason: Railway uses trapezoid to show load spreading (40° angle)
                            const ballastHeight = baseCourseHeightPx;
                            const subballastHeight = subbaseCourseHeightPx;
                            const slopeRatio = 0.84; // 40 degrees angle (tan(40°) ≈ 0.84)

                            // Subballast trapezoid: continues from ballast bottom edge
                            const trainWheelWidth = imageToRenderWidth + 60;
                            const topStartX = rectStartX + squareWidth - trainWheelWidth - ballastHeight * slopeRatio;
                            const topEndX = rectStartX + squareWidth;

                            // Bottom edge spreads further based on subballast height
                            const subballastSlopeOffset = subballastHeight * slopeRatio;
                            const bottomStartX = topStartX - subballastSlopeOffset;
                            const bottomEndX = topEndX; // Right edge stays aligned

                            context.beginPath();
                            context.moveTo(topStartX, subbaseY); // Top left
                            context.lineTo(topEndX, subbaseY); // Top right
                            context.lineTo(bottomEndX, subbaseY + subballastHeight); // Bottom right
                            context.lineTo(bottomStartX, subbaseY + subballastHeight); // Bottom left
                            context.closePath();

                            // Apply fill color and texture
                            if (texturesLoaded.texture3 && texture3Img) {
                                const pattern = context.createPattern(texture3Img, 'repeat');
                                if (pattern) {
                                    context.fillStyle = pattern;
                                }
                            } else {
                                context.fillStyle = fallbackColors.subbase;
                            }
                            context.fill();

                            // Apply stroke
                            context.strokeStyle = 'grey';
                            context.lineWidth = 0.5;
                            context.stroke();

                            context.fillStrokeShape(shape);
                        }}
                    />
                ) : (
                    <Rect
                        x={Math.max(0, rectStartX)}
                        y={Math.max(0, subbaseY)}
                        width={Math.max(1, squareWidth)}
                        height={Math.max(1, subbaseCourseHeightPx)}
                        fillPatternImage={texturesLoaded.texture3 && texture3Img ? texture3Img : undefined}
                        fillPatternRepeat="repeat"
                        fillPatternScale={{ x: 0.5, y: 0.5 }}
                        fill={!texturesLoaded.texture3 || !texture3Img ? fallbackColors.subbase : undefined}
                        stroke="grey"
                        strokeWidth={0.5}
                    />
                )}
                {/* === Sub grade === */}
                {/* Render Subgrade wider for Railway to eliminate step between Subballast and Subgrade */}
                {geogridType === 'Railway' ? (
                    <Shape
                        sceneFunc={(context, shape) => {
                            // Reason: Railway subgrade starts at same width as subballast bottom edge to eliminate visual step
                            const ballastHeight = baseCourseHeightPx;
                            const subballastHeight = subbaseCourseHeightPx;
                            const slopeRatio = 0.84;
                            const trainWheelWidth = imageToRenderWidth + 60;

                            // Subgrade left edge starts at subballast bottom left edge
                            const subgradeStartX = rectStartX + squareWidth - trainWheelWidth - (ballastHeight + subballastHeight) * slopeRatio;
                            const subgradeWidth = squareWidth - (squareWidth - trainWheelWidth - (ballastHeight + subballastHeight) * slopeRatio);

                            context.beginPath();
                            context.moveTo(subgradeStartX, subgradeY);
                            context.lineTo(rectStartX + squareWidth, subgradeY);
                            context.lineTo(rectStartX + squareWidth, subgradeY + subgradeHeightPx);
                            context.lineTo(subgradeStartX, subgradeY + subgradeHeightPx);
                            context.closePath();

                            // Apply fill color and texture
                            if (texturesLoaded.texture4 && texture4Img) {
                                const pattern = context.createPattern(texture4Img, 'repeat');
                                if (pattern) {
                                    context.fillStyle = pattern;
                                }
                            } else {
                                context.fillStyle = fallbackColors.subgrade;
                            }
                            context.fill();

                            // Apply stroke
                            context.strokeStyle = 'grey';
                            context.lineWidth = 0.5;
                            context.stroke();

                            context.fillStrokeShape(shape);
                        }}
                    />
                ) : (
                    <Rect
                        x={Math.max(0, rectStartX)}
                        y={Math.max(0, subgradeY)}
                        width={Math.max(1, squareWidth)}
                        height={Math.max(1, subgradeHeightPx)}
                        fillPatternImage={texturesLoaded.texture4 && texture4Img ? texture4Img : undefined}
                        fillPatternRepeat="repeat"
                        fillPatternScale={{ x: 0.4, y: 0.4 }}
                        fill={!texturesLoaded.texture4 || !texture4Img ? fallbackColors.subgrade : undefined}
                        stroke="grey"
                        strokeWidth={0.5}
                    />
                )}
                {/* Subgrade label rendered after shape for proper z-index (appears on top) */}
                <Text text={titles[2]} x={5} y={subgradeY + subgradeHeightPx / 2 - 6} width={titleAreaWidth - 10} align="right" fontSize={12} fill="black" />

                {/* === GEOGRIDS - Rendered on top of all layers === */}
                {/* Base Course Geogrids */}
                {baseCourseGeogrids?.map((geogrid, index) => {
                    const positionMeters = Math.max(0, geogrid.position ?? 0);
                    const selectedGeogrid = geogrid.selectedGeogrid ?? 'N/A';
                    if (positionMeters > baseCourseThickness) return null;
                    const geogridY = Math.max(0, baseY + baseCourseHeightPx - positionMeters * baseCourseScale);

                    // For Railway trapezoid, calculate geogrid width based on position in trapezoid
                    let lineStartX = rectStartX;
                    let lineEndX = rectEndX;
                    if (geogridType === 'Railway') {
                        const slopeRatio = 0.84;
                        const trainWheelWidth = imageToRenderWidth + 60;
                        const distanceFromTop = baseCourseHeightPx - (positionMeters * baseCourseScale);
                        const topStartX = rectStartX + squareWidth - trainWheelWidth;
                        lineStartX = topStartX - distanceFromTop * slopeRatio;
                        lineEndX = rectEndX;
                    }

                    return (
                        <React.Fragment key={`base-grid-frag-${index}`}>
                            <Group y={geogridY}>
                                <Line points={[lineStartX, 0, lineEndX, 0]} stroke={geogridColor} strokeWidth={3} shadowColor="#FFFFFF" opacity={0.8} lineCap="round" />
                                {circleXs
                                    .filter(x => x + 6 >= lineStartX && x + 6 <= lineEndX)
                                    .map((x, idx) => (
                                        <Circle key={idx} x={x + 6} y={0} radius={cerchioRaggio} fill="white" stroke={geogridColor} strokeWidth={2} shadowColor="#FFFFFF" opacity={1} />
                                    ))}
                                <Group
                                    x={rectStartX + squareWidth / 2 - (Math.max(0, geogridBaseTextWidths[index] || 0) + geogridLabelPadding) / 2}
                                    y={-(Math.max(0, geogridBaseTextHeights[index] || 0) + geogridLabelPadding / 2)}>
                                    <Rect
                                        width={Math.max(1, (geogridBaseTextWidths[index] || 0) + geogridLabelPadding)}
                                        height={Math.max(1, (geogridBaseTextHeights[index] || 0) + geogridLabelPadding / 2)}
                                        fill="#FFFFFF"
                                        cornerRadius={6}
                                        x={0}
                                        y={-10}
                                        stroke="#828282"
                                        strokeWidth={2}
                                    />
                                    <Text
                                        ref={el => (geogridBaseTextRefs.current[index] = el)}
                                        text={selectedGeogrid}
                                        fontSize={12}
                                        fontStyle="bold"
                                        fill="#000000"
                                        x={geogridLabelPadding / 2}
                                        y={-4}
                                    />
                                </Group>
                            </Group>
                            {/* dimensioni a destra */}
                            <Text text={`${positionMeters.toFixed(2)} ${unitLabel}`} x={labelX} y={geogridY - 6} fontSize={font_right_dimension} fill="black" fontStyle="bold" />
                        </React.Fragment>
                    );
                })}

                {/* Subbase Course Geogrids */}
                {subbaseCourseGeogrids?.map((geogrid, index) => {
                    const positionMeters = Math.max(0, geogrid.position ?? 0);
                    const selectedGeogrid = geogrid.selectedGeogrid ?? 'N/A';
                    if (positionMeters > subbaseCourseThickness) return null;
                    const geogridY = Math.max(0, subbaseY + subbaseCourseHeightPx - positionMeters * subbaseCourseScale);

                    // For Railway trapezoid, calculate geogrid width based on position in trapezoid
                    let lineStartX = rectStartX;
                    let lineEndX = rectEndX;
                    if (geogridType === 'Railway') {
                        const slopeRatio = 0.84;
                        const trainWheelWidth = imageToRenderWidth + 60;
                        const ballastHeight = baseCourseHeightPx;
                        const distanceFromBallastTop = ballastHeight + (subbaseCourseHeightPx - positionMeters * subbaseCourseScale);
                        const topStartX = rectStartX + squareWidth - trainWheelWidth;
                        lineStartX = topStartX - distanceFromBallastTop * slopeRatio;
                        lineEndX = rectEndX;
                    }

                    return (
                        <React.Fragment key={`subbase-grid-frag-${index}`}>
                            <Group y={geogridY}>
                                <Line points={[lineStartX, 0, lineEndX, 0]} stroke={geogridColor} strokeWidth={3} shadowColor="#FFFFFF" opacity={0.8} lineCap="round" />
                                {circleXs
                                    .filter(x => x + 6 >= lineStartX && x + 6 <= lineEndX)
                                    .map((x, idx) => (
                                        <Circle key={idx} x={x + 6} y={0} radius={cerchioRaggio} fill="white" stroke={geogridColor} strokeWidth={2} shadowColor="#FFFFFF" opacity={1} />
                                    ))}
                                <Group
                                    x={rectStartX + squareWidth / 2 - (Math.max(0, geogridSubbaseTextWidths[index] || 0) + geogridLabelPadding) / 2}
                                    y={-(Math.max(0, geogridSubbaseTextHeights[index] || 0) + geogridLabelPadding / 2)}>
                                    <Rect
                                        width={Math.max(1, (geogridSubbaseTextWidths[index] || 0) + geogridLabelPadding)}
                                        height={Math.max(1, (geogridSubbaseTextHeights[index] || 0) + geogridLabelPadding / 2)}
                                        fill="#FFFFFF"
                                        cornerRadius={6}
                                        x={0}
                                        y={-10}
                                        stroke="#828282"
                                        strokeWidth={2}
                                    />
                                    <Text
                                        ref={el => (geogridSubbaseTextRefs.current[index] = el)}
                                        text={selectedGeogrid}
                                        fontSize={12}
                                        fontStyle="bold"
                                        fill="#000000"
                                        x={geogridLabelPadding / 2}
                                        y={-4}
                                    />
                                </Group>
                            </Group>
                            {/* dimensioni a destra */}
                            <Text text={`${positionMeters.toFixed(2)} ${unitLabel}`} x={labelX} y={geogridY - 6} fontSize={font_right_dimension} fill="black" fontStyle="bold" />
                        </React.Fragment>
                    );
                })}

                {/* Rettangolo "trasparente" con solo bordo */}
                {/* <Rect
          x={0}
          y={0}
          width={stageWidth}
          height={totalHeight}
          fillEnabled={false} // Nessun riempimento, solo bordo
          stroke="pink"       // Bordo rosa
          strokeWidth={4}     // Puoi aumentare se vuoi più spesso
        /> */}
            </Layer>
        </Stage>
    );
};

export default GeogridLayers;
